import React, { useState } from 'react';
import { Box, CssBaseline, ThemeProvider, Toolbar } from '@mui/material';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';

// Auth
import { AuthProviderWithRouter, useAuth } from './components/Auth/AuthContext';

// Theme
import { AppThemeProvider, useAppTheme } from './contexts/ThemeContext';

// Layout
import Sidebar from './components/Sidebar/Sidebar';
import Navbar  from './components/Sidebar/Navbar';

// Pages
import Dashboard  from './components/Dashboard/Dashboard';
import Properties from './components/Properties/Properties';
import Tenants    from './components/Tenants/Tenants';
import Payments   from './components/Payments/Payments';
import Reports    from './components/Reports/Reports';
import Settings   from './components/Settings/Settings';
import Login      from './components/Auth/Login';
import Register   from './components/Auth/Register';
import WaterBills from './components/WaterBills/WaterBills';

// ─── Main layout ─────────────────────────────────────────────────────────────

function MainLayout({ children }) {
  const [drawerOpen, setDrawerOpen]   = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const { logout }  = useAuth();
  const { palette } = useAppTheme();
  const navigate    = useNavigate();

  const toggleDrawer        = () => setDrawerOpen(prev => !prev);
  const handleSidebarToggle = (collapsed) => setIsCollapsed(collapsed);
  const handleLogout        = () => { logout(); navigate('/login'); };

  return (
    <Box sx={{ display: 'flex', backgroundColor: palette.background.primary, minHeight: '100vh' }}>
      <CssBaseline />

      <Navbar isCollapsed={isCollapsed} onMenuClick={toggleDrawer} />

      <Sidebar
        open={drawerOpen}
        toggleDrawer={toggleDrawer}
        handleLogout={handleLogout}
        onToggle={handleSidebarToggle}
      />

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 0,
          width: '100%',
          overflow: 'auto',
          backgroundColor: palette.background.secondary,
          minHeight: '100vh',
        }}
      >
        <Toolbar sx={{ height: 80 }} />
        {children}
      </Box>
    </Box>
  );
}

// ─── Protected route ──────────────────────────────────────────────────────────

function ProtectedRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();
  const { palette } = useAppTheme();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', backgroundColor: palette.background.primary }}>
        Loading...
      </Box>
    );
  }

  if (!isAuthenticated) return <Navigate to="/login" replace />;

  return <MainLayout>{children}</MainLayout>;
}

// ─── Routes ───────────────────────────────────────────────────────────────────

function AppRoutes() {
  const { isAuthenticated } = useAuth();

  return (
    <Routes>
      <Route path="/login"    element={isAuthenticated ? <Navigate to="/dashboard" /> : <Login />} />
      <Route path="/register" element={isAuthenticated ? <Navigate to="/dashboard" /> : <Register />} />

      <Route path="/dashboard"  element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/properties" element={<ProtectedRoute><Properties /></ProtectedRoute>} />
      <Route path="/tenants"    element={<ProtectedRoute><Tenants /></ProtectedRoute>} />
      <Route path="/water-bills" element={<ProtectedRoute><WaterBills /></ProtectedRoute>} />
      <Route path="/payments"   element={<ProtectedRoute><Payments /></ProtectedRoute>} />
      <Route path="/reports"    element={<ProtectedRoute><Reports /></ProtectedRoute>} />
      <Route path="/settings"   element={<ProtectedRoute><Settings /></ProtectedRoute>} />

      <Route path="/"  element={<Navigate to="/login" />} />
      <Route path="*"  element={<Navigate to={isAuthenticated ? '/dashboard' : '/login'} />} />
    </Routes>
  );
}

// ─── Root ─────────────────────────────────────────────────────────────────────

// Needs to be a child of AppThemeProvider so it can read muiTheme
function AppWithTheme() {
  const { muiTheme } = useAppTheme();
  return (
    <ThemeProvider theme={muiTheme}>
      <AppRoutes />
    </ThemeProvider>
  );
}

function App() {
  return (
    <Router>
      <AppThemeProvider>
        <AuthProviderWithRouter>
          <AppWithTheme />
        </AuthProviderWithRouter>
      </AppThemeProvider>
    </Router>
  );
}

export default App;